/**
 * ─── ⭐⭐ WHO TOUCHED THE TOKEN, AND WHAT THEY PUT THERE ──────────────────────────────────────────
 *
 * Temporary instrumentation for one specific unsolved failure (2026-10-26): log in on production,
 * receive a good 12-hour token, click one feature link, and arrive back at the login page with an
 * EXPIRED token in storage. Measured twice, 8:57pm and 9:07pm, and not reproducible on demand —
 * the third and fourth attempts that evening were clean.
 *
 * ⛔ EVERY OTHER DIAGNOSTIC WE HAVE LOOKS AT THE AFTERMATH. The breadcrumbs say a token was dead
 * when it was read; they cannot say who wrote it, or what was there before. Six places in this
 * client write `authToken`, and the stale value could equally come from a seventh nobody has
 * thought of — which is exactly the shape of thing a hand-placed log line keeps missing.
 *
 * So this wraps the storage call itself. A same-tab write does NOT fire a `storage` event — that
 * only reaches other tabs — so patching is the only way to see every writer in one place,
 * including ones we have not guessed.
 *
 * ⚠️ REMOVE THIS once the cause is known. It is deliberately self-contained and installed from one
 * line in index.js: delete the file, delete the import, and nothing else has to be unpicked.
 *
 * ⛔ NEVER RECORDS THE TOKEN, in keeping with the breadcrumbs. Shape, expiry and the writing call
 * site are everything a diagnosis needs, and a credential in a diagnostic is a credential in a
 * bug report.
 */

const TOKEN_KEY = 'authToken';
export const TOKEN_WRITE_KEY = 'authTokenWrites';
const MAX_WRITES = 24;

/** Shape and expiry of a token, never the token. */
function shapeOf(raw) {
  if (raw === null || raw === undefined) return { shape: 'absent', expiresAt: null, expired: null };
  if (raw === 'null' || raw === 'undefined') return { shape: `poisoned:"${raw}"`, expiresAt: null, expired: null };
  const out = { shape: `len=${raw.length} parts=${raw.split('.').length}`, expiresAt: null, expired: null };
  try {
    const b64 = raw.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4)));
    if (typeof payload.exp === 'number') {
      out.expiresAt = new Date(payload.exp * 1000).toISOString();
      out.expired = payload.exp * 1000 < Date.now();
    }
  } catch (_) { out.shape += ' (unreadable)'; }
  return out;
}

/**
 * The call site, as a couple of frames of stack.
 *
 * ⓘ Production bundles are minified, so these read as `main.abc123.js:2:45678` rather than a
 * function name. That is still decisive: it distinguishes one writer from another, and a source
 * map turns it back into a line.
 *
 * ⚠️ A FIXED OFFSET, NOT A FILENAME FILTER. Dropping frames whose text mentions this file looks
 * tidier and is wrong twice over: minification renames the file, and a caller that legitimately
 * lives in a file with a similar name gets thrown away — which is how the first version of this
 * reported jest's internals instead of the test that called it. The three frames above the caller
 * are always callSite → record → the patched storage method, in every engine we run on.
 */
const OWN_FRAMES = 3;

function callSite() {
  try {
    const frames = String(new Error().stack || '')
      .split('\n')
      .filter(f => f && !/^\s*Error\s*$/.test(f));
    return frames
      .slice(OWN_FRAMES, OWN_FRAMES + 3)
      .map(f => f.trim())
      .join(' | ');
  } catch (_) { return 'unavailable'; }
}

/**
 * ⛔ A SIGN-OUT IS NOT A DOWNGRADE. Logging out removes a perfectly live token on purpose, which
 * is the exact shape of the bug — so without this, every sign-out would raise the one alarm this
 * file exists to raise, and the real one would be lost among them.
 *
 * ⓘ The key is read as a literal rather than imported from apiClient, which owns it
 * (DELIBERATE_SIGNOUT_KEY): this module is installed from index.js before anything else loads, and
 * it should not be the reason the API client and axios get pulled in early. Logout sets the marker
 * BEFORE clearing the token, so it is already in place when this runs.
 */
function signingOutDeliberately() {
  try { return sessionStorage.getItem('signedOutDeliberately') !== null; } catch (_) { return false; }
}

function record(action, before, after) {
  try {
    const deliberate = signingOutDeliberately();
    let history = [];
    try { history = JSON.parse(localStorage.getItem(TOKEN_WRITE_KEY)) || []; } catch (_) { history = []; }
    if (!Array.isArray(history)) history = [];

    const from = shapeOf(before);
    const to = shapeOf(after);

    /**
     * ⛔ A WRITE THAT CHANGED NOTHING IS NOT EVIDENCE, AND IT COSTS EVIDENCE.
     *
     * The login page clears the token on mount whether or not one is there, so every arrival at
     * /login logs a removal of nothing. Measured on dev 2026-10-27: seven of the first nine
     * entries were exactly that, which in a 24-entry ring means the noise would evict the
     * transition this instrument exists to catch, in a matter of minutes.
     */
    if (from.shape === 'absent' && to.shape === 'absent') return;
    /**
     * ⭐ THE ONE LINE WORTH READING. A downgrade is the bug: a live token replaced by a dead one,
     * or by nothing, while it still had life left. Everything else here is ordinary bookkeeping.
     */
    const downgrade = from.expired === false
      && !deliberate
      && (to.shape === 'absent' || to.expired === true
        || (!!from.expiresAt && !!to.expiresAt && to.expiresAt < from.expiresAt));

    const entry = {
      at: new Date().toISOString(),
      action,
      page: window?.location?.pathname || null,
      downgrade,
      deliberate,
      from: from.shape,
      fromExpiresAt: from.expiresAt,
      to: to.shape,
      toExpiresAt: to.expiresAt,
      by: callSite(),
    };

    history.push(entry);
    while (history.length > MAX_WRITES) history.shift();
    localStorage.setItem(TOKEN_WRITE_KEY, JSON.stringify(history));

    if (downgrade) {
      console.warn('⛔ A LIVE TOKEN WAS DOWNGRADED — this is the bug. Paste this:', entry);
    }
  } catch (_) { /* a diagnostic must never break the thing it is watching */ }
}

/** The recorded writes, newest last. For the console: `readTokenWrites()`. */
export function readTokenWrites() {
  try { return JSON.parse(localStorage.getItem(TOKEN_WRITE_KEY)) || []; } catch (_) { return []; }
}

/**
 * ⓘ Returns how many entries it discarded. A console helper that returns nothing prints
 * `undefined`, which reads as a failure — it cost the operator two false alarms on 2026-10-27.
 */
export function clearTokenWrites() {
  try {
    const had = readTokenWrites().length;
    localStorage.removeItem(TOKEN_WRITE_KEY);
    return `cleared ${had} token write${had === 1 ? '' : 's'}`;
  } catch (_) { return 'cleared (storage unavailable)'; }
}

let installed = false;

/**
 * Patch `setItem` and `removeItem` for this one key. Called once, as early as possible.
 *
 * ⚠️ The patch delegates to the original immediately and records afterwards, so a throwing
 * diagnostic can never stop a token from being stored. And it guards against double-installation,
 * because wrapping a wrapper would double every entry and make the history read as a cascade.
 */
export function installTokenAudit() {
  if (installed || typeof window === 'undefined' || !window.localStorage) return;
  installed = true;

  const proto = Object.getPrototypeOf(window.localStorage) || Storage.prototype;
  const realSet = proto.setItem;
  const realRemove = proto.removeItem;

  proto.setItem = function patchedSetItem(key, value) {
    if (key !== TOKEN_KEY) return realSet.call(this, key, value);
    let before = null;
    try { before = window.localStorage.getItem(TOKEN_KEY); } catch (_) { before = null; }
    const result = realSet.call(this, key, value);
    record('set', before, String(value));
    return result;
  };

  proto.removeItem = function patchedRemoveItem(key) {
    if (key !== TOKEN_KEY) return realRemove.call(this, key);
    let before = null;
    try { before = window.localStorage.getItem(TOKEN_KEY); } catch (_) { before = null; }
    const result = realRemove.call(this, key);
    record('remove', before, null);
    return result;
  };

  window.readTokenWrites = readTokenWrites;
  window.clearTokenWrites = clearTokenWrites;
}
